/**
 * "Not on this building's census? Find in eMerge" -- inside "Record a day's TWL Q" (director, 2026-10-28).
 *
 * A resident eMerge already holds, whom the provider qualified at this building, is sometimes not on the building's
 * census when the review opened (the review's lines are seeded only from the confirmed census), so the end-of-day
 * tally had no line to attach them to and the reviewer listed them as missing. This finds them among the residents
 * eMerge holds (GET /manifest-intake/residents/find: a name, or an exact CounselEAR id; the reviewer's buildings),
 * says in words where eMerge has them now, and adds the pick to the day.
 *
 * What it never does: create a resident, write a CounselEAR id or a name, or move anybody. Recording the day adds a
 * line to this building's review and records the tally's word; the resident stays where eMerge has them. The server
 * decides again who may be recorded (POST /twl-day refuses a resident outside the reviewer's reach).
 */
import React, { useEffect, useRef, useState } from 'react';
import { manifestApi, type ResidentFindHit } from './manifestApi';
import { StateBlock as TideState } from '../../components/tide';
import { NAME_NOT_RECORDED } from './board/residents';

/** A resident picked from eMerge for this day: what the form needs to show and record them. */
export type FoundResident = { patient_id: string; label: string; where: string };

/** Where eMerge has the resident now, in words, against the building being recorded. */
export function whereWords(h: Pick<ResidentFindHit, 'facility_id' | 'facility_name'>, facility: { id: string; name: string }): string {
  if (h.facility_id && h.facility_id.toLowerCase() === facility.id.toLowerCase()) return `eMerge has them at ${facility.name}, but not on this day's review.`;
  if (h.facility_id) return `eMerge has them at ${h.facility_name || 'another building'}, not at ${facility.name}.`;
  return 'eMerge has them with no building yet.';
}

export const DOES_NOT_MOVE = 'Recording the day adds them to this building\'s review and records the tally\'s word. It does not move them: eMerge keeps them where they are.';

export default function FindInEmerge({ facility, visitDateLabel, taken, onPick }: {
  facility: { id: string; name: string };
  visitDateLabel: string;
  /** Residents already on the day (the census lines and earlier picks): offered as "already on this day". */
  taken: Set<string>;
  onPick: (r: FoundResident) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ResidentFindHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    seq.current += 1;
    const mine = seq.current;
    if (term.length < 2) { setHits(null); setProblem(null); return undefined; }
    const t = window.setTimeout(async () => {
      setBusy(true);
      try {
        const r = await manifestApi.findResident(term);
        if (mine !== seq.current) return;
        if (r.success && r.data) { setHits(r.data.items); setProblem(null); } else { setHits(null); setProblem(r.message || r.error || 'eMerge could not search just now.'); }
      } catch {
        if (mine === seq.current) { setHits(null); setProblem('Could not reach eMerge.'); }
      } finally {
        if (mine === seq.current) setBusy(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  if (!open) {
    return (
      <p className="mf-quiet">
        Not on this building&rsquo;s census?{' '}
        <button type="button" className="mf-linkbtn" onClick={() => setOpen(true)}>Find in eMerge</button>
      </p>
    );
  }

  return (
    <section className="mf-find-emerge" aria-label="Find a resident in eMerge">
      <label><span>Find in eMerge</span>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, or the CounselEAR id" autoComplete="off" autoFocus />
      </label>
      <p className="mf-quiet">Residents eMerge already holds, at the buildings you review. Nobody is created here.</p>
      {problem && <p className="mf-note warn" role="alert">{problem}</p>}
      {busy && hits === null && <p className="mf-quiet" role="status">Searching…</p>}
      {hits && hits.length === 0 && (
        <TideState kind="empty" reason="filtered" message={<>eMerge holds nobody you review who matches that. A resident eMerge does not hold cannot be added here.</>} />
      )}
      {hits && hits.length > 0 && (
        <ul className="mf-picks">
          {hits.map((h) => {
            const label = h.name || (h.counselear_id ? `CounselEAR ${h.counselear_id} (name not recorded)` : NAME_NOT_RECORDED);
            const where = whereWords(h, facility);
            const already = taken.has(h.id.toLowerCase());
            return (
              <li key={h.id}>
                <div>
                  <div className="mf-name"><strong>{h.name || NAME_NOT_RECORDED}</strong>{h.counselear_id ? <span className="mf-quiet"> · CE {h.counselear_id}</span> : null}</div>
                  <p className="mf-quiet">{where}</p>
                </div>
                <button type="button" className="mf-go ghost" disabled={already} onClick={() => onPick({ patient_id: h.id, label, where })}
                  aria-label={already ? `${label} is already on this day` : `Add ${label} to ${facility.name}, ${visitDateLabel}`}>
                  {already ? 'Already on this day' : 'Add to this day'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="mf-note" role="note">{DOES_NOT_MOVE}</p>
    </section>
  );
}
